import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  InputAdornment,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import pedidoVentaService, { PedidoVenta } from '../../services/pedidoVentaService';
import cotizacionVentaService, { CotizacionVenta } from '../../services/cotizacionVentaService';
import { formatCurrency } from '../../utils/formatters';

export type FacturarOrigenTipo = 'pedido' | 'cotizacion';

interface FacturarOrigenDialogProps {
  open: boolean;
  tipo: FacturarOrigenTipo;
  nroCliente?: number | null;
  onClose: () => void;
  onSelect: (nroFormulario: number) => void;
}

type OrigenRow = {
  nroFormulario: number;
  nombre?: string;
  nroCliente?: number;
  estado?: string;
  fechaEmision?: string | Date;
  importeTotal?: number;
  letra?: string;
  nroSuc?: number;
  nroComprobante?: number;
};

function formatComprobante(row: OrigenRow): string {
  const letra = (row.letra ?? '').trim();
  if (!letra || row.nroSuc == null || row.nroComprobante == null) {
    return `#${row.nroFormulario}`;
  }
  const suc = String(row.nroSuc).padStart(5, '0');
  const nro = String(row.nroComprobante).padStart(8, '0');
  return `${letra.toUpperCase()}-${suc}-${nro}`;
}

function formatFecha(value?: string | Date): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-AR');
}

const ESTADOS_FACTURABLES = ['Nuevo', 'Impreso', 'Preparado'] as const;

function estadoFacturable(estado?: string | null): boolean {
  const key = (estado || 'Nuevo').trim();
  return (ESTADOS_FACTURABLES as readonly string[]).includes(key);
}

function puedeFacturarPedido(p: PedidoVenta): boolean {
  return estadoFacturable(p.estado);
}

function puedeFacturarCotizacion(c: CotizacionVenta): boolean {
  return estadoFacturable(c.estado);
}

function sortOrigenByFechaDesc(a: OrigenRow, b: OrigenRow): number {
  const ta = a.fechaEmision ? new Date(a.fechaEmision).getTime() : 0;
  const tb = b.fechaEmision ? new Date(b.fechaEmision).getTime() : 0;
  return tb - ta;
}

function dedupeOrigenRows(rows: OrigenRow[]): OrigenRow[] {
  const seen = new Set<number>();
  const out: OrigenRow[] = [];
  for (const row of rows) {
    if (seen.has(row.nroFormulario)) continue;
    seen.add(row.nroFormulario);
    out.push(row);
  }
  return out;
}

function mapPedidoRow(p: PedidoVenta): OrigenRow {
  const ext = p as PedidoVenta & {
    letra?: string;
    nroSuc?: number;
    nroComprobante?: number;
  };
  return {
    nroFormulario: p.nroFormulario,
    nombre: p.nombre,
    nroCliente: p.nroCliente,
    estado: p.estado,
    fechaEmision: p.fechaEmision,
    importeTotal: p.importeTotal,
    letra: ext.letra,
    nroSuc: ext.nroSuc,
    nroComprobante: ext.nroComprobante,
  };
}

function mapCotizacionRow(c: CotizacionVenta): OrigenRow {
  const ext = c as CotizacionVenta & {
    letra?: string;
    nroSuc?: number;
    nroComprobante?: number;
  };
  return {
    nroFormulario: c.nroFormulario,
    nombre: c.nombre,
    nroCliente: c.nroCliente,
    estado: c.estado,
    fechaEmision: c.fechaEmision,
    importeTotal: c.importeTotal,
    letra: ext.letra,
    nroSuc: ext.nroSuc,
    nroComprobante: ext.nroComprobante,
  };
}

/**
 * Diálogo para elegir un pedido (F6) o cotización (F9) a facturar.
 */
const FacturarOrigenDialog: React.FC<FacturarOrigenDialogProps> = ({
  open,
  tipo,
  nroCliente,
  onClose,
  onSelect,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<OrigenRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const titulo = tipo === 'pedido' ? 'Facturar pedido (F6)' : 'Facturar cotización (F9)';
  const vacío =
    tipo === 'pedido'
      ? 'No hay pedidos facturables para este criterio.'
      : 'No hay cotizaciones facturables para este criterio.';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const nroClienteStr =
        nroCliente && nroCliente > 0 ? String(nroCliente) : undefined;
      const term = search.trim() || undefined;

      // El API filtra un solo estado; pedimos Nuevo / Impreso / Preparado y unimos.
      if (tipo === 'pedido') {
        const pages = await Promise.all(
          ESTADOS_FACTURABLES.map((estado) =>
            pedidoVentaService.getPedidosVenta({
              pageNumber: 1,
              pageSize: 20,
              searchTerm: term,
              nroCliente: nroClienteStr,
              estado,
            }),
          ),
        );
        const merged = pages.flatMap((res) => (res.items || []).filter(puedeFacturarPedido).map(mapPedidoRow));
        setRows(dedupeOrigenRows(merged).sort(sortOrigenByFechaDesc));
      } else {
        const pages = await Promise.all(
          ESTADOS_FACTURABLES.map((estado) =>
            cotizacionVentaService.getCotizacionesVenta({
              pageNumber: 1,
              pageSize: 20,
              searchTerm: term,
              nroCliente: nroClienteStr,
              estado,
            }),
          ),
        );
        const merged = pages
          .flatMap((res) => (res.items || []).filter(puedeFacturarCotizacion).map(mapCotizacionRow));
        setRows(dedupeOrigenRows(merged).sort(sortOrigenByFechaDesc));
      }
    } catch (e: unknown) {
      const message =
        (e as { response?: { data?: unknown }; message?: string })?.response?.data != null
          ? String((e as { response: { data: unknown } }).response.data)
          : (e as { message?: string })?.message || 'Error al cargar el listado';
      setError(message);
      setRows([]);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [tipo, nroCliente, search, enqueueSnackbar]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setSearch('');
    setError(null);
    setRows([]);
  }, [open, tipo]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const t = window.setTimeout(() => {
      void load();
    }, search ? 350 : 50);
    return () => window.clearTimeout(t);
  }, [open, load, search]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{titulo}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          autoFocus
          placeholder={
            tipo === 'pedido'
              ? 'Buscar pedido por cliente, comprobante…'
              : 'Buscar cotización por cliente, comprobante…'
          }
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {nroCliente && nroCliente > 0 ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Filtrado por cliente #{nroCliente}. Estados: Nuevo, Impreso y Preparado.
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Sin cliente en la factura: se listan Nuevo, Impreso y Preparado.
          </Typography>
        )}
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 && !error ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {vacío}
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Total
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">
                    Acción
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.nroFormulario} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatComprobante(row)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatFecha(row.fechaEmision)}</TableCell>
                    <TableCell>
                      {row.nroCliente ? `${row.nroCliente} - ` : ''}
                      {row.nombre || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip label={row.estado || 'Nuevo'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{formatCurrency(row.importeTotal || 0)}</TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onSelect(row.nroFormulario)}
                      >
                        Facturar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button onClick={() => void load()} disabled={loading}>
          Actualizar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FacturarOrigenDialog;
